import { useQueryClient } from '@tanstack/react-query';
import {
  FindMembers,
  MemberFilters,
  MembersList,
} from 'components/Pages/CarrierMembers';
import {
  ListingPageHeader,
  Paginator,
  ConfirmationDialog,
  ConfirmationDialogRef,
} from 'components/Shared';
import { usePaginationCache } from 'hooks';
import { Scaffold } from 'layouts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from 'store';
import { ImpersonateActions } from 'store/ducks/auth';
import { PaginateCarrierMembersActions as PaginateActions } from 'store/ducks/carrierMembers';
import * as S from './styles';

export const CarrierMembersListPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const dialogRef = useRef<ConfirmationDialogRef>(null);
  const queryClient = useQueryClient();

  const { paginationCache, updatePaginationCache, handleFilter, handleSort } =
    usePaginationCache('carrierMembers');

  const {
    data: members,
    pagination,
    loading,
  } = useSelector((state: RootState) => state.paginateCarrierMembers);

  const [query, setQuery] = useState<FindMembers>({
    search: '',
    page: 1,
    limit: 10,
    ...paginationCache,
  });

  const onPageChange = useCallback((page: number): void => {
    setQuery((state) => ({ ...state, page }));
  }, []);

  const onQueryChange = useCallback((): void => {
    dispatch(PaginateActions.request(query));
  }, [dispatch, query]);

  const onImpersonate = useCallback(
    async (email: string): Promise<void> => {
      const confirmed = await dialogRef.current?.openDialog({
        title: 'Logar como outro usuário?',
        message:
          'Você está prestes a logar como outro usuário. Deseja continuar?',
      });

      if (confirmed) {
        const onSuccess = async () => {
          await queryClient.invalidateQueries();
          navigate('/agendamento/solicitacoes/carregamento');
        };
        dispatch(ImpersonateActions.request({ email }, onSuccess));
      }
    },
    [dispatch, navigate, queryClient]
  );

  useEffect(() => {
    onQueryChange();
  }, [onQueryChange]);

  useEffect(() => {
    return () => {
      dispatch(PaginateActions.reset());
      updatePaginationCache(query);
    };
  }, [dispatch, query, updatePaginationCache]);

  return (
    <Scaffold>
      <ConfirmationDialog ref={dialogRef} />
      <S.MainPanel>
        <ListingPageHeader
          icon={<S.UserRectangleIcon />}
          title="Usuários de transportadoras"
          isLoading={loading}
          actions={
            <S.LinkButton
              to={'/configuracoes/transportadoras/usuarios/criar'}
              size="small"
            >
              <S.PlusIcon /> Novo usuário
            </S.LinkButton>
          }
        />
        <MemberFilters
          currentFilter={query}
          onFilter={(filter) => handleFilter(query, filter, setQuery)}
        />
        <MembersList
          members={members}
          currentSort={query}
          onImpersonate={onImpersonate}
          onSort={(sort) => handleSort(query, sort, setQuery)}
        />
        <Paginator onPageChange={onPageChange} pagination={pagination} />
      </S.MainPanel>
    </Scaffold>
  );
};
